import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isMediaAnchor, isTextAnchor, type EntryAttachment } from '@discovr/types';
import { ArrowBendUpLeft, PencilSimple, Trash } from '@phosphor-icons/react';
import { AlertCircle, Loader2, MessageCircle, RefreshCw, Send, X } from '../../../icons';
import { Button } from '../../ui/button';

import { cn } from '../../../lib/utils';
import type { MediaMessageAnchor, MessageAnchor, TextMessageAnchor } from '../../../lib/models';
import { AttachFilesButton } from './AttachFilesButton';
import { AttachmentChips } from './AttachmentChips';
import { DaySeparator, JumpFlash, RejectionEntry, type FlashTarget } from './ChatEntryParts';
import {
  dayKey,
  DELETE_MESSAGE_LABEL,
  DELETE_TITLE,
  EDIT_MESSAGE_LABEL,
  EDIT_TITLE,
  formatTime,
  hasAnyAction,
  isLongBody,
  isMine,
  isOptimisticEntry,
  isRejectionEntry,
  messagePermissions,
  newOptimisticEntryId,
  senderDisplayName,
  senderLabelFor,
  showsBodyText,
  type ChatThreadEntry,
  type MessageActionGates,
  type MessagePermissions,
} from './chatEntries';
import { COMPOSER_CHIP_CLASS, COMPOSER_CHIP_DISMISS_CLASS, COMPOSER_CHIP_TITLE_CLASS } from './chatClasses';
import { FileViewerDialog } from './FileViewer/FileViewerDialog';
import type { ViewerItem } from './FileViewer/viewerModel';
import type { FileSource } from './fileSource';
import { MessageAttachments } from './MessageAttachments';
import { MomentBlock } from './MomentBlock';
import { NoteCardList, type NoteThreadContext } from './NoteCards';
import { StagedMomentChip } from './StagedMomentChip';
import {
  attachmentViewerItems,
  canSendDraft,
  frameViewerItem,
  MOMENT_DROPPED,
  momentFlags,
  NO_STAGED_FILES,
  REPLY_DISCARD_CONFIRM_LABEL,
  REPLY_DISCARD_DESCRIPTION,
  REPLY_DISCARD_TITLE,
  replyPreviewText,
  sendCarriesMoment,
  stagedMomentAnchor,
  toOptimisticAttachments,
  type FilePicker,
  type RichComposer,
} from './moments';
import { ScrollArea } from '../../ui/scroll-area';
import { Skeleton } from '../../ui/skeleton';
import { ActionConfirmDialog } from '../ActionConfirmDialog';

export type { ChatThreadEntry } from './chatEntries';
export type { FilePicker, MomentSendOutcome, RichComposer, StagedFileState, StagedMoment } from './moments';

/** What sending a message hands the wrapping panel. An object, not
 *  positional args: `anchor` sits in the middle of the old (body, anchor,
 *  replyTo) signature, so a caller that doesn't support anchors (Content
 *  Review, Creator Review) had to name-and-discard it (`(body, _anchor,
 *  replyTo) => ...`) just to reach `replyTo` — easy to typo/reorder and
 *  silently drop the wrong field. A named field can't be positionally
 *  confused with another, and an adapter that doesn't care about `anchor`
 *  just never destructures it. */
export type AddCommentPayload = { body: string; anchor?: MessageAnchor; replyTo?: string };

/**
 * How messages are drawn: `bubbles` — the WhatsApp-style bubbles every
 * surface has always had (script and selection threads keep them); `cards` —
 * the content review's note cards, with replies nested inside their parent's
 * card (see NoteCards).
 */
export type ChatThreadLayout = 'bubbles' | 'cards';
export const CHAT_LAYOUT_BUBBLES: ChatThreadLayout = 'bubbles';
export const CHAT_LAYOUT_CARDS: ChatThreadLayout = 'cards';

export type ChatThreadProps = {
  entries?: ChatThreadEntry[];

  /** Message layout; `bubbles` unless a surface opts into `cards`. */
  layout?: ChatThreadLayout;

  /** Header label above the messages (e.g. "Conversation"). When provided,
   *  renders a Figma-styled header strip (18px/500). Wrapping panels that
   *  provide their own header should leave this unset. */
  title?: string;

  /** Optional close handler — renders an X button inside the title header.
   *  Typically used to collapse the chat column from within the thread. */
  onClose?: () => void;

  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;

  canAddComment?: boolean;
  /** Thread is locked (approved → read-only): nothing on it can be posted,
   *  edited or deleted. Distinct from `canAddComment`, which answers "may THIS
   *  viewer post?" — a role-based posting restriction must not also revoke the
   *  viewer's ability to edit or delete their own past messages on an open
   *  thread. Omitted: falls back to `canAddComment === false`, which is what
   *  every current caller means by it. */
  locked?: boolean;
  addCommentBusy?: boolean;
  onAddComment?: (payload: AddCommentPayload) => void | Promise<void>;

  /** Quote-reply anchor staged above the composer (script annotations).
   *  Owned by the wrapping panel; sent with the next message. */
  stagedAnchor?: TextMessageAnchor;
  /** Dismisses the staged anchor — called by the chip's × (no argument)
   *  and after a successful anchored send (with the SENT anchor, so the
   *  owner only clears it if the user hasn't staged a new one since). */
  onDismissAnchor?: (sent?: TextMessageAnchor) => void;
  /** Clicking a message's quote-reply anchor block jumps the script view
   *  to that line (and highlights it). Omit to render the quote as plain,
   *  non-interactive text — e.g. surfaces with no script pane to jump to. */
  onJumpToAnchor?: (anchor: TextMessageAnchor) => void;

  /** The reverse direction of onJumpToAnchor: an external trigger (the
   *  Script tab's "View comment" hover pill) asking THIS thread to scroll
   *  to and flash one of its own messages — reuses the exact scroll+flash
   *  jumpToMessage already does internally for a reply-preview click.
   *  Nonce-keyed so clicking the SAME message twice in a row still
   *  re-triggers (a same-id update alone is a no-op React bails on). */
  scrollToMessageId?: { id: string; nonce: number } | null;

  /** Enables reply-to nesting: a Reply affordance on bubbles and
   *  single-level indented rendering of replies under their root.
   *  Off by default — panels whose send handlers don't forward replyTo
   *  must not show a Reply button that would silently flatten. */
  enableReplies?: boolean;

  /** Read-only viewer: keep the composer visible but disable the input +
   *  send button and surface a tooltip. Distinct from the locked state
   *  (`canAddComment === false`), which hides the composer entirely. */
  readOnly?: boolean;

  draft?: string;
  setDraft?: (v: string) => void;
  /** Composer is an auto-growing `<textarea>` (1–4 lines, then scrolls). */
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;

  outgoingActor?: string;

  /** True only for the Super Admin tier. Super Admin's delete is
   *  unconditional server-side, including on a locked (approved) thread —
   *  the only role for which that's true. Every other viewer's delete/edit
   *  must still gate on the local `threadLocked`/`readOnly` state so the
   *  button doesn't round-trip a 423. */
  viewerIsSuperAdmin?: boolean;
  /** Edit a message (body only). Per-message eligibility is entirely
   *  server-computed (entry.viewer_can_edit) — omit to hide Edit everywhere. */
  onEditMessage?: (id: string, body: string) => void | Promise<void>;
  /** Delete a message. Per-message eligibility is entirely server-computed
   *  (entry.viewer_can_delete) — omit to hide Delete everywhere. */
  onDeleteMessage?: (id: string) => void | Promise<void>;

  emptyStateText?: string;
  composerPlaceholder?: string;
  className?: string;

  /** Seeks the player to a message's moment. Makes the time pill clickable
   *  and adds Go to frame to the file viewer. Omit on surfaces with no player. */
  onSeekToMoment?: (tMs: number) => void;
  /** The content version playing now; a moment made on any other version is
   *  labelled "earlier cut" and can't be sought. */
  currentContentVersionId?: string;
  /** Content panel composer extension: tools row, staged moment and file
   *  chips, the Attach button (when `filePicker` is wired), and every send
   *  carrying a moment or files — replies included — routed to `onSend`.
   *  Omit everywhere else. */
  richComposer?: RichComposer;
  /** Turns an attached file's (or a sent frame's) id into bytes, for the
   *  bubble's thumbnails and the file viewer. Omit on surfaces without files:
   *  attachments then show as name-only tiles, and a sent frame shows no
   *  thumbnail. */
  fileSource?: FileSource;
  /** Told when the file viewer opens and closes (an unmount while it's open
   *  counts as closed) — so a surface with its own page-wide shortcuts can
   *  leave the viewer's keys to it. Pass a stable function (a state setter,
   *  or a `useCallback`): a new one while the viewer is open reports closed
   *  and then open again. */
  onViewerOpenChange?: (open: boolean) => void;
};

/** A reply staged from a bubble's Reply button; rides the next send. */
type StagedReply = { id: string; label: string; preview: string };
/** A Reply click held back while the user confirms dropping a staged frame. */
type PendingReply = { entry: ChatThreadEntry; label: string };
/** The open file viewer: what it steps through, where it is, and — for a
 *  moment's frame — the moment Go to frame seeks to. */
type OpenViewer = { items: ViewerItem[]; index: number; frameAnchor?: MediaMessageAnchor };

/** Tools row (Capture frame / Add timestamp) above the composer input. */
const MOMENT_TOOLS_ROW_CLASS = 'mb-2 flex flex-wrap items-center gap-2';
/** Space between a message's text and its files. */
const ATTACHMENTS_BELOW_TEXT_CLASS = 'mt-2';
const NO_ATTACHMENTS: EntryAttachment[] = [];
const NO_VIEWER_ITEMS: ViewerItem[] = [];
/** A moment's frame is the viewer's only item. */
const FRAME_ITEM_INDEX = 0;
const EMPTY_DRAFT = '';

/** The composer as it is now — for a send that settles renders after it started. */
type ComposerNow = { draft: string; editing: boolean };

/** A failed send's text goes back only into an empty input that isn't editing — never over what the user has written since. */
function canRestoreDraft({ draft, editing }: ComposerNow): boolean {
  return !editing && !draft.trim();
}

/** Go to frame is offered only when the open frame's moment is seekable here. */
function frameSeekable(
  anchor: MediaMessageAnchor | undefined,
  currentContentVersionId?: string,
  onSeekToMoment?: (tMs: number) => void,
): boolean {
  if (!anchor) return false;
  return momentFlags(anchor, currentContentVersionId, onSeekToMoment).seekable;
}

function viewerAt(viewer: OpenViewer | null, index: number): OpenViewer | null {
  if (!viewer) return null;
  return { ...viewer, index };
}

/**
 * The Attach button's picker, or undefined when there's no button: no picker
 * wired, or the composer can't stage anything right now (an edit, which never
 * carries files, or a read-only viewer).
 */
function attachPickerFor(composer: RichComposer | undefined, canStage: boolean): FilePicker | undefined {
  if (!canStage) return undefined;
  return composer?.filePicker;
}

export function ChatThread({
  entries,
  layout = CHAT_LAYOUT_BUBBLES,
  title,
  onClose,
  isLoading = false,
  isError = false,
  onRetry,
  canAddComment,
  locked,
  addCommentBusy,
  onAddComment,
  stagedAnchor,
  onDismissAnchor,
  onJumpToAnchor,
  scrollToMessageId,
  enableReplies = false,
  readOnly = false,
  draft: draftProp,
  setDraft: setDraftProp,
  textareaRef: textareaRefProp,
  outgoingActor = 'brand',
  viewerIsSuperAdmin = false,
  onEditMessage,
  onDeleteMessage,
  emptyStateText = 'No messages yet. Start the conversation by leaving a note below.',
  composerPlaceholder = 'Type a message…',
  className,
  onSeekToMoment,
  currentContentVersionId,
  richComposer,
  fileSource,
  onViewerOpenChange,
}: ChatThreadProps) {
  // Explicit `locked` wins; otherwise keep the historical reading of
  // canAddComment as the lock signal so existing callers are unaffected.
  const threadLocked = locked ?? !canAddComment;
  // That fallback is the trap this prop exists to remove: a caller whose
  // canAddComment encodes anything other than lock state (a role restriction, a
  // "is a post handler wired?" check) silently loses Edit/Delete on an open
  // thread. `locked` is optional for back-compat, so TypeScript cannot demand
  // it — surface it at dev time instead of failing silently. Ref-guarded in an
  // effect so it fires once per offending mount, not on every keystroke.
  const warnedMissingLockedRef = useRef(false);
  const missingLocked = locked === undefined && !!(onEditMessage || onDeleteMessage);
  useEffect(() => {
    if (!import.meta.env?.DEV || !missingLocked || warnedMissingLockedRef.current) return;
    warnedMissingLockedRef.current = true;
    console.warn(
      '[ChatThread] onEditMessage/onDeleteMessage supplied without `locked`; ' +
        'falling back to `!canAddComment`. Pass an explicit `locked` — if this ' +
        "caller's canAddComment is not purely lock state, Edit/Delete will be " +
        'hidden on an unlocked thread.',
    );
  }, [missingLocked]);
  const [internalDraft, setInternalDraft] = useState('');
  const internalRef = useRef<HTMLTextAreaElement | null>(null);
  const draft = draftProp ?? internalDraft;
  const setDraft = setDraftProp ?? setInternalDraft;
  const [expandedMsgs, setExpandedMsgs] = useState<Set<string>>(() => new Set());
  const toggleExpanded = (id: string): void =>
    setExpandedMsgs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const textareaRef = textareaRefProp ?? internalRef;
  // Auto-grow the composer with content (1–4 lines), then switch to its own
  // scrollbar — re-measured on every draft change, not just onChange, so a
  // draft set externally (e.g. "Add to comment") grows it too.
  //
  // The 4-line cap is computed from the textarea's own metrics rather than
  // a fixed px class: a hardcoded max-height only happens to fit N lines
  // for one exact combination of font-size/line-height/padding, and
  // silently clips a line short the moment any of those changes.
  const COMPOSER_MAX_ROWS = 4;
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // border-box sizing: `height` is the OUTER box (content + padding +
    // border), but scrollHeight is the inner box (content + padding only).
    // Setting height straight to scrollHeight comes up border-width short —
    // content then overflows by that same amount, showing a permanent
    // sliver scrollbar even on an empty, single-line textarea.
    const cs = getComputedStyle(el);
    const borderY = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    const paddingY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const lineHeight = parseFloat(cs.lineHeight);
    const maxHeight = lineHeight * COMPOSER_MAX_ROWS + paddingY + borderY;
    const contentHeight = el.scrollHeight + borderY;
    el.style.height = `${Math.min(contentHeight, maxHeight)}px`;
    el.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
  }, [draft, textareaRef]);

  const [optimistic, setOptimistic] = useState<ChatThreadEntry[]>([]);
  // Last send failed — surfaced above the composer (a silent catch made
  // rejected sends look like the feature was broken). Cleared on typing.
  const [sendFailed, setSendFailed] = useState(false);
  // Reply target staged from a bubble's Reply button; rides the next send.
  const [stagedReply, setStagedReply] = useState<StagedReply | null>(null);
  // Reply awaiting the "discard the attached frame?" confirm (rich composer only).
  const [pendingReply, setPendingReply] = useState<PendingReply | null>(null);
  // The file viewer, open on a message's files or on a moment's frame.
  const [viewer, setViewer] = useState<OpenViewer | null>(null);
  // Reported from an effect rather than the open/close handlers so the
  // cleanup also reports "closed" when the chat unmounts with the viewer up.
  const viewerOpen = viewer !== null;
  useEffect(() => {
    if (!viewerOpen || !onViewerOpenChange) return undefined;
    onViewerOpenChange(true);
    return function reportViewerClosed(): void {
      onViewerOpenChange(false);
    };
  }, [viewerOpen, onViewerOpenChange]);
  // Message being edited: its ORIGINAL body is previewed in the composer
  // banner (WhatsApp-style) and loaded into the input for editing; the next
  // submit rewrites it in place instead of sending a new message.
  const [editing, setEditing] = useState<{ id: string; preview: string } | null>(null);
  // Read by a send that settles later: the input may have moved on since it started.
  const composerNowRef = useRef<ComposerNow>({ draft, editing: false });
  useLayoutEffect(() => {
    composerNowRef.current = { draft, editing: editing !== null };
  }, [draft, editing]);
  // Message pending delete confirmation — drives the in-app confirm dialog
  // (no native window.confirm) and its in-flight state.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const threadContentRef = useRef<HTMLDivElement | null>(null);
  const prevThreadLenRef = useRef(0);
  // A send (or edit) is in flight. It guards Enter-to-send, which has no
  // disabled state of its own, so a double Enter can't send the same anchor
  // on two messages; and it disables Send meanwhile. State rather than a ref
  // so the button reflects it — a keypress's update renders before the next
  // keypress arrives, so the guard still holds.
  const [sending, setSending] = useState(false);

  // WhatsApp-style "tap the quoted preview to jump to the original message":
  // one-shot flash, cleared on its own timer — not driven by scrollToQuote
  // state like the script-anchor jump, since this never needs to survive a
  // tab switch or an external trigger, only a click inside this thread.
  //
  // Keyed by a NONCE, not just the target id: clicking the SAME preview
  // again while the previous flash is still mid-animation set state to the
  // same id, which React bails on (no re-render) — the overlay div was
  // never remounted, so its CSS `animation` never restarted, but the
  // timeout clearing it away kept getting pushed out by each click. Once
  // the (never-restarted) animation naturally finished at 1.6s but the div
  // was still up (deletion delayed by a later click), it had no
  // in-progress animation left to hold it at opacity 0 — it reverted to
  // its unanimated base, fully opaque, painting a solid dark purple block
  // over the message. The nonce forces a fresh `key` on every click, so
  // React always remounts a brand-new div with its animation starting
  // clean, however rapid the clicks.
  const [flashTarget, setFlashTarget] = useState<FlashTarget | null>(null);
  const flashNonceRef = useRef(0);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jumpToMessage = (id: string) => {
    const target = threadContentRef.current?.querySelector<HTMLElement>(
      `[data-message-id="${CSS.escape(id)}"]`,
    );
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Move focus too, not just the scroll position — a keyboard/AT user
    // who activates this control (vs. a mouse user who visually sees the
    // scroll + flash) otherwise has focus stay on the control they just
    // pressed, with no indication anything happened. Needs tabIndex={-1}
    // on the target (see data-message-id below) to be a valid focus target
    // despite not being normally tab-reachable.
    target.focus({ preventScroll: true });
    if (flashTimeoutRef.current !== null) clearTimeout(flashTimeoutRef.current);
    flashNonceRef.current += 1;
    setFlashTarget({ id, nonce: flashNonceRef.current });
    const FLASH_MS = 1600;
    flashTimeoutRef.current = setTimeout(() => {
      flashTimeoutRef.current = null;
      setFlashTarget(null);
    }, FLASH_MS);
  };
  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current !== null) clearTimeout(flashTimeoutRef.current);
    };
  }, []);

  // External trigger for jumpToMessage (the Script tab's "View comment"
  // hover pill) — see scrollToMessageId's own doc comment. Depends on the
  // prop's own object identity, not its contents: the owner (ScriptReviewPanel)
  // already constructs a fresh {id, nonce} on every click, even a repeat
  // click on the same message, so identity alone is enough to retrigger —
  // same convention as ScriptResultTabs' scrollToQuote.
  useEffect(() => {
    if (!scrollToMessageId) return;
    jumpToMessage(scrollToMessageId.id);
  }, [scrollToMessageId]);

  // Optimistic bubbles whose send is still in flight, each with whether the
  // thread grew while it was. Growth stands for "the server echo arrived", so
  // it clears settled bubbles — but a send in flight keeps its bubble: a rich
  // send uploads for a while, and someone else's message mustn't take it down
  // (nor leave a failed send with no bubble to roll back).
  const inFlightBubblesRef = useRef(new Map<string, boolean>());

  useEffect(() => {
    const len = Array.isArray(entries) ? entries.length : 0;
    if (len > prevThreadLenRef.current) {
      const inFlight = inFlightBubblesRef.current;
      inFlight.forEach((_, id) => inFlight.set(id, true));
      setOptimistic((prev) => prev.filter((e) => inFlight.has(e.id)));
    }
    prevThreadLenRef.current = len;
  }, [entries]);

  const sorted = useMemo(() => {
    const list = Array.isArray(entries) ? [...entries] : [];
    const ids = new Set(list.map((e) => e.id));
    for (const e of optimistic) if (!ids.has(e.id)) list.push(e);
    list.sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
    });
    return list;
  }, [entries, optimistic]);

  // WhatsApp-style replies: strict chronological order; a reply carries a
  // compact quoted preview of its parent INSIDE the bubble (both sides).
  // reply_to pointing outside the loaded page simply shows no preview.
  const byId = useMemo(() => new Map(sorted.map((m) => [m.id, m] as const)), [sorted]);

  // Opening a thread with existing history jumps straight to the bottom
  // (instant) — same as WhatsApp/Slack, no reason to animate through a
  // long backlog on first paint. A message arriving AFTER that (the
  // user's own send, a reply, an SSE push) scrolls smoothly instead, so
  // it reads as a natural continuation of the conversation rather than a
  // jarring jump.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      block: 'end',
      behavior: hasMountedRef.current ? 'smooth' : 'auto',
    });
    hasMountedRef.current = true;
  }, [sorted.length]);

  // Load a message into the composer for an in-place edit. Clears any staged
  // reply/anchor — an edit rewrites existing text, it isn't a new message.
  const beginEdit = (entry: ChatThreadEntry) => {
    setStagedReply(null);
    setSendFailed(false);
    setEditing({ id: entry.id, preview: entry.body });
    setDraft(entry.body);
    textareaRef.current?.focus();
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraft('');
  };
  // Reply supersedes an in-progress edit: leaving an untouched edit to reply
  // instead must switch cleanly, so drop the edit (and its draft) first, then
  // stage the reply. Without this the composer stayed in edit mode and the
  // reply never took (the flow the user hit).
  const startReply = (entry: ChatThreadEntry, label: string) => {
    if (editing) cancelEdit();
    setSendFailed(false);
    setStagedReply({ id: entry.id, label, preview: replyPreviewText(entry) });
    textareaRef.current?.focus();
  };
  // A reply carries the original message's moment, so a staged moment can't
  // ride along: a staged frame is real work, so ask before discarding it; a
  // staged timestamp alone is dropped silently — unless a send in flight is
  // carrying it, which clears it once it lands. Staged files may ride a
  // reply, so they're left as they are.
  const beginReply = (entry: ChatThreadEntry, label: string) => {
    const staged = richComposer?.staged;
    if (staged?.frameUrl) {
      setPendingReply({ entry, label });
      return;
    }
    const dropsStagedTimestamp = !!staged && !sending;
    if (dropsStagedTimestamp) richComposer?.onClear();
    startReply(entry, label);
  };
  const confirmDiscardAndReply = () => {
    const pending = pendingReply;
    setPendingReply(null);
    if (!pending) return;
    richComposer?.onClear();
    startReply(pending.entry, pending.label);
  };
  const handleReplyDiscardOpenChange = (next: boolean) => {
    if (!next) setPendingReply(null);
  };
  const closeViewer = () => setViewer(null);
  // A bubble's frame thumbnail opens that frame in the viewer. The item is
  // built now, from the entry's own snapshot (`local_frame_url`), so a later
  // change in the composer never repaints an open viewer.
  const openFrameFor = (entry: ChatThreadEntry, anchor: MediaMessageAnchor) => function openFrame() {
    setViewer({ items: [frameViewerItem(anchor, entry.local_frame_url)], index: FRAME_ITEM_INDEX, frameAnchor: anchor });
  };
  // A thumbnail or tile opens the viewer on that file, stepping through the
  // message's files. An optimistic bubble's files have no uploaded ids to
  // fetch yet, so they don't open until the send lands.
  const attachmentOpenerFor = (attachments: EntryAttachment[], optimistic: boolean) => {
    if (optimistic || !fileSource) return undefined;
    return function openAttachment(index: number) {
      setViewer({ items: attachmentViewerItems(attachments, fileSource), index });
    };
  };
  const changeViewerIndex = (index: number | null) => {
    if (index === null) {
      closeViewer();
      return;
    }
    setViewer((current) => viewerAt(current, index));
  };
  const goToFrame = (tMs: number) => {
    onSeekToMoment?.(tMs);
    closeViewer();
  };
  const confirmDelete = async () => {
    const id = deletingId;
    if (!id || !onDeleteMessage) return;
    setDeleteBusy(true);
    try {
      await onDeleteMessage(id);
      if (editing?.id === id) cancelEdit();
      setDeletingId(null);
    } catch {
      // Close the dialog so it doesn't sit open looking inert — the caller's
      // onDeleteMessage surfaces the reason (e.g. a "locked" toast). The
      // composer's sendFailed banner is hidden on locked threads, so relying
      // on it alone would leave a failed delete with no visible feedback.
      setDeletingId(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  // A send takes the text out of the input at once. Noted in the ref too, so a
  // send that fails before the next render still finds the input empty.
  const takeDraftForSend = () => {
    composerNowRef.current = { ...composerNowRef.current, draft: EMPTY_DRAFT };
    setDraft(EMPTY_DRAFT);
  };

  // An unsent optimistic bubble comes back down and its text goes back into
  // the input — no failure banner (the owner has already told the user why).
  // The input stays editable while a send runs, so text typed (or an edit
  // begun) meanwhile is kept rather than overwritten.
  const withdrawOptimistic = (tempId: string, body: string) => {
    inFlightBubblesRef.current.delete(tempId);
    setOptimistic((prev) => prev.filter((e) => e.id !== tempId));
    if (!canRestoreDraft(composerNowRef.current)) return;
    setDraft(body);
  };

  const showOptimistic = (temp: ChatThreadEntry): void => {
    inFlightBubblesRef.current.set(temp.id, false);
    setOptimistic((prev) => [...prev, temp]);
  };

  // A sent bubble gives way to the server's message: at once if the thread
  // grew while it was in flight (the echo may already be showing), otherwise
  // on the next growth, as before.
  const settleOptimistic = (tempId: string): void => {
    const grewWhileInFlight = inFlightBubblesRef.current.get(tempId);
    inFlightBubblesRef.current.delete(tempId);
    if (!grewWhileInFlight) return;
    setOptimistic((prev) => prev.filter((e) => e.id !== tempId));
  };

  // Shared failure path for optimistic sends: drop the bubble, give the text
  // back and show the "Couldn't send" banner.
  const rollbackFailedSend = (tempId: string, body: string) => {
    withdrawOptimistic(tempId, body);
    setSendFailed(true);
  };

  // Closes the reply a send just answered — unless the user has since staged
  // a reply to a different message.
  const closeSentReply = (replyTo?: string): void => {
    if (!replyTo) return;
    setStagedReply((cur) => (cur?.id === replyTo ? null : cur));
  };

  // Send with a staged moment and/or files: the optimistic bubble shows them
  // at once (the local frame and image previews). The owner clears what it
  // staged only on success, so a failed send keeps everything attached for
  // the retry — the reply stays open too, so the retry answers the same
  // message. A `dropped` outcome (the moment's cut is gone, or nothing was
  // staged) is not a failure: the text comes back without the "Couldn't
  // send" banner.
  const submitRich = async (body: string, composer: RichComposer, replyTo?: string): Promise<void> => {
    // A reply uses the original message's moment, so it never shows the staged one.
    const staged = sendCarriesMoment(replyTo) ? composer.staged : null;
    const temp: ChatThreadEntry = {
      id: newOptimisticEntryId(),
      actor: outgoingActor,
      body,
      anchor: staged ? stagedMomentAnchor(staged, currentContentVersionId) : undefined,
      reply_to: replyTo,
      created_at: new Date().toISOString(),
      // Snapshotted now, not re-read later — see ChatThreadEntry's doc comment.
      local_frame_url: staged?.frameUrl,
      attachments: toOptimisticAttachments(composer.files),
    };
    setSending(true);
    setSendFailed(false);
    showOptimistic(temp);
    takeDraftForSend();
    try {
      const outcome = await composer.onSend(body, replyTo);
      if (outcome === MOMENT_DROPPED) {
        withdrawOptimistic(temp.id, body);
        return;
      }
      settleOptimistic(temp.id);
      closeSentReply(replyTo);
    } catch {
      rollbackFailedSend(temp.id, body);
    } finally {
      setSending(false);
    }
  };

  const stagedMoment = richComposer?.staged ?? null;
  const stagedFiles = richComposer?.files ?? NO_STAGED_FILES;
  const hasStagedFiles = stagedFiles.length > 0;
  // A reply never carries the moment, so a timestamp still staged from a send
  // that failed mid-reply must not route a text-only reply through the rich
  // send, which would hand the text straight back with nothing sent.
  const sendsRich = hasStagedFiles || (!!stagedMoment && !stagedReply);

  // A moment staged from outside the chat ("Note at", Add timestamp, a
  // capture) while a reply is open cancels the reply — the moment wins, since
  // a reply can't carry one. Only the null → staged edge counts, so an owner
  // handing a fresh-but-equal object each render never cancels a new reply.
  // The typed text stays in the input.
  const hadStagedMomentRef = useRef(!!stagedMoment);
  useEffect(() => {
    const hadStagedMoment = hadStagedMomentRef.current;
    hadStagedMomentRef.current = !!stagedMoment;
    if (hadStagedMoment || !stagedMoment) return;
    setStagedReply(null);
  }, [stagedMoment]);

  const sendable = canSendDraft({
    hasText: !!draft.trim(),
    editing: !!editing,
    replying: !!stagedReply,
    hasStagedFrame: !!stagedMoment?.frameUrl,
    hasStagedFiles,
  });

  const submit = async () => {
    if (readOnly || sending) return;
    const t = draft.trim();
    if (!sendable) return;
    // Edit path: rewrite the staged message in place instead of sending new.
    if (editing) {
      if (!onEditMessage) return;
      // No change → just close the editor, don't call the BE (and don't stamp
      // an "edited" marker for a no-op edit).
      if (t === editing.preview.trim()) {
        cancelEdit();
        return;
      }
      setSending(true);
      setSendFailed(false);
      try {
        await onEditMessage(editing.id, t);
        setEditing(null);
        setDraft('');
      } catch {
        setSendFailed(true);
      } finally {
        setSending(false);
      }
      return;
    }
    if (richComposer && sendsRich) {
      await submitRich(t, richComposer, stagedReply?.id);
      return;
    }
    if (!onAddComment) return;
    const anchor = stagedAnchor;
    const reply = stagedReply;
    const temp: ChatThreadEntry = {
      id: newOptimisticEntryId(),
      actor: outgoingActor,
      body: t,
      anchor,
      reply_to: reply?.id,
      created_at: new Date().toISOString(),
    };
    setSending(true);
    setSendFailed(false);
    showOptimistic(temp);
    takeDraftForSend();
    // Dismiss the staged anchor/reply in the SAME tick the optimistic
    // bubble goes up, not after the network round-trip resolves. The quote
    // is already captured into `temp` and visible in that bubble
    // immediately — leaving the staged chip up above the composer until
    // the request settled showed the same quote twice (once staged, once
    // already sent) for the round-trip's duration, and the chip's
    // disappearance visibly lagged the send instead of tracking it.
    // Trade-off: unlike the old after-the-await dismissal, a FAILED send
    // no longer leaves the quote re-attachable for a retry — the optimistic
    // bubble (and the quote inside it) is removed below same as before, so
    // there's nothing left showing it either way; the user re-selects the
    // text if they want to retry with the anchor attached. Passing the sent
    // anchor/reply (not a bare clear) still lets the owner keep any NEW
    // quote/reply the user staged in the instant between click and here.
    if (anchor) onDismissAnchor?.(anchor);
    setStagedReply((cur) => (cur === reply ? null : cur));
    try {
      await onAddComment({ body: t, anchor, replyTo: reply?.id });
      settleOptimistic(temp.id);
    } catch {
      rollbackFailedSend(temp.id, t);
    } finally {
      setSending(false);
    }
  };

  const composerDisabled = readOnly || isError || (isLoading && sorted.length === 0);
  // Send is off while the owner reports busy or this thread has a send in
  // flight — a rich send keeps its files staged until it settles, so
  // `sendable` alone would leave Send looking live while it ignores clicks.
  const sendBusy = !!addCommentBusy || sending;
  // Rich composer surfaces: the tools row only for a fresh message; the staged
  // chips and the Attach button everywhere but an edit (an edit never carries
  // a moment or files).
  const showMomentTools = !!richComposer && !stagedReply && !editing && !readOnly;
  const canStageRich = !editing && !readOnly;
  const showStagedMomentChip = canStageRich && !!stagedMoment && !!richComposer;
  const showFileChips = canStageRich && hasStagedFiles;
  const attachPicker = attachPickerFor(richComposer, canStageRich);
  const viewerFrame = viewer?.frameAnchor;
  const goToViewerFrame = () => {
    if (viewerFrame) goToFrame(viewerFrame.t_ms);
  };
  const viewerGoToFrame = frameSeekable(viewerFrame, currentContentVersionId, onSeekToMoment) ? goToViewerFrame : undefined;
  const readOnlyTitle = readOnly ? 'You have read-only access to this campaign' : undefined;

  const actionGates: MessageActionGates = {
    enableReplies,
    canAddComment: !!canAddComment,
    canPost: !!onAddComment,
    canEditAny: !!onEditMessage,
    canDeleteAny: !!onDeleteMessage,
    threadLocked,
    readOnly,
    viewerIsSuperAdmin,
  };
  const permissionsFor = (entry: ChatThreadEntry): MessagePermissions => messagePermissions(entry, actionGates);
  const usesCards = layout === CHAT_LAYOUT_CARDS;
  const noteThread: NoteThreadContext = {
    outgoingActor,
    flashTarget,
    expanded: expandedMsgs,
    onToggleExpanded: toggleExpanded,
    permissionsFor,
    onReply: beginReply,
    onEdit: beginEdit,
    onDelete: setDeletingId,
    currentContentVersionId,
    onSeekToMoment,
    fileSource,
    openFrameFor,
    attachmentOpenerFor,
  };

  return (
    <section className={cn('flex h-full min-h-0 flex-1 flex-col bg-card', className)}>
      {/* WhatsApp-style "jump to original message": a soft tinted overlay
          fading to nothing over the bubble — not a border/ring, which reads
          as a validation state, not a momentary "you're here" pulse. */}
      <style>{`@keyframes chat-jump-flash { from { opacity: 0.35; } to { opacity: 0; } }`}</style>
      {/* Conversation header — Figma: 18px/500/#1B1A17 */}
      {title ? (
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-4">
          <span className="text-heading-h7/7 font-medium text-[#1B1A17]">{title}</span>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close conversation"
              className="flex size-6 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
            >
              <X size={13} />
            </button>
          ) : null}
        </div>
      ) : null}

      <ScrollArea className="min-h-0 flex-1">
        <div ref={threadContentRef} className="px-[24px] py-[22px]">
          {isError ? (
            <ErrorState onRetry={onRetry} />
          ) : isLoading && sorted.length === 0 ? (
            <SkeletonBubbles />
          ) : sorted.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center text-body-sm-regular text-muted-foreground">
              <MessageCircle size={24} className="text-muted-foreground" />
              <p className="mt-2 max-w-xs text-caption-regular">{emptyStateText}</p>
            </div>
          ) : usesCards ? (
            <NoteCardList entries={sorted} thread={noteThread} />
          ) : (
            (() => {
              const rendered: React.ReactNode[] = [];
              let lastDay = '';
              for (let i = 0; i < sorted.length; i += 1) {
                const e = sorted[i];
                if (!e) continue;
                const prev = i > 0 ? sorted[i - 1] : undefined;
                const next = i < sorted.length - 1 ? sorted[i + 1] : undefined;
                const k = dayKey(e.created_at);
                if (k !== lastDay) {
                  rendered.push(<DaySeparator key={`day-${k}`} iso={e.created_at} />);
                  lastDay = k;
                }

                if (isRejectionEntry(e)) {
                  rendered.push(<RejectionEntry key={e.id} entry={e} outgoingActor={outgoingActor} />);
                  continue;
                }

                const mine = isMine(e.actor, outgoingActor);
                const parent = e.reply_to ? byId.get(e.reply_to) : undefined;
                const sameSenderAsPrev =
                  prev && prev.actor === e.actor && (prev.kind ?? '') === (e.kind ?? '') && dayKey(prev.created_at) === k;
                const sameSenderAsNext =
                  next && next.actor === e.actor && (next.kind ?? '') === (e.kind ?? '') && dayKey(next.created_at) === k;
                const isFirstOfGroup = !sameSenderAsPrev;
                const isLastOfGroup = !sameSenderAsNext;

                // Sender display name for label ("<name> · <org>") and the
                // avatar initial derived from it, so a name-less account shows
                // its brand initial rather than a role word's.
                const senderName = senderLabelFor(e, outgoingActor);
                const initial = mine ? 'Y' : (senderName.charAt(0) || '?').toUpperCase();

                const isOptimistic = isOptimisticEntry(String(e.id));
                const permissions = permissionsFor(e);
                const { canReply: canReplyMsg, canEdit: canEditMsg, canDelete: canDeleteMsg } = permissions;
                const timeStr = formatTime(e.created_at);
                // Narrowed local, not e.anchor! at each use — a property
                // access doesn't stay narrowed inside the onClick/onKeyDown
                // closures below, a plain const does. Only the script-quote
                // variant renders here; media anchors are handled separately.
                const anchor = isTextAnchor(e.anchor) ? e.anchor : undefined;
                const mediaAnchor = isMediaAnchor(e.anchor) ? e.anchor : undefined;
                const attachments = e.attachments ?? NO_ATTACHMENTS;
                const bodyTextShown = showsBodyText(e.body, attachments);

                rendered.push(
                  <div
                    key={e.id}
                    className={cn(
                      'group flex items-end gap-2.5',
                      mine ? 'flex-row-reverse' : 'flex-row',
                      // Tight stack within a consecutive run from the same
                      // sender (Instagram/Telegram/WhatsApp-style); a real
                      // gap only between distinct groups.
                      isLastOfGroup ? 'mb-4' : 'mb-0.5',
                    )}
                  >
                    {/* Avatar (Figma 4635:90181/90183): 24px circle, dimmed
                        ground, sender photo when available — identical
                        treatment on both sides. Shown on last of group;
                        invisible placeholder otherwise for alignment. */}
                    <div
                      className={cn(
                        'size-6 shrink-0 overflow-hidden rounded-full bg-[#868e96] flex items-center justify-center text-[10px] font-medium text-white',
                        !isLastOfGroup && 'invisible',
                      )}
                      aria-hidden
                    >
                      {e.sender_avatar ? (
                        <img src={e.sender_avatar} alt="" className="size-full object-cover" />
                      ) : (
                        initial
                      )}
                    </div>

                    {/* Bubble column — max-w-[80%] lives HERE, not on the bubble
                        below. The column's own containing block (the message
                        row) has a definite width; the bubble's containing
                        block (this column) does not, since the column
                        shrink-to-fits its content. A percentage max-width on
                        the bubble would resolve against that self-referential
                        auto width — bubble ends up capped to 80% of its own
                        hugged size, which can be narrower than even a single
                        short word, forcing it to wrap for no reason. Capping
                        the column instead (against the row's real width) and
                        letting the bubble fill it (block default) breaks the
                        cycle. */}
                    <div
                      className={cn(
                        'flex min-w-0 max-w-[80%] flex-col',
                        mine ? 'items-end' : 'items-start',
                      )}
                    >
                      {/* Sender (first of group only) — the exact time now sits
                          inline on every bubble below, matching WhatsApp/
                          Telegram, so it isn't repeated up here. */}
                      {isFirstOfGroup ? (
                        <div className="mb-1 text-caption-regular/4 font-normal text-[#A3A3A3]">
                          {senderName}
                        </div>
                      ) : null}

                      {/* Bubble — relative so the hover-reveal reply icon
                          (below) can sit pinned to its bottom-right corner.
                          data-message-id is the target of jumpToMessage
                          (tapping a reply preview below). tabIndex={-1}:
                          not normally tab-reachable, but a valid target for
                          jumpToMessage's own target.focus() call so a
                          keyboard/AT user's focus actually moves here, not
                          just the scroll position. */}
                      <div
                        data-message-id={e.id}
                        tabIndex={-1}
                        className={cn(
                          'relative p-[12px] text-body-md-regular/6',
                          // A reply bubble must fit its quoted-parent preview.
                          parent && 'min-w-[228px]',
                          mine
                            ? 'rounded-[8px_8px_0px_8px] bg-[#F4ECFE] text-[#1A0540]'
                            : 'rounded-[8px_8px_8px_0px] bg-[#F4F4F5] text-[#000000]',
                        )}
                      >
                        <JumpFlash target={flashTarget} messageId={e.id} />
                        {/* WhatsApp-style reply preview: who + what this message
                            answers, quoted inside the bubble on both sides.
                            bg-black/5 read as almost the same tone as both
                            bubble colors (#F4ECFE / #F4F4F5), so the quoted
                            card and the sender's own words below it blended
                            into one block — bg-white/70 gives it real
                            contrast against either, mb-2.5 a clearer gap.
                            Clickable — tapping it jumps to (and flashes) the
                            original message, same as WhatsApp. Silently
                            inert when the parent fell outside the loaded
                            page (no `parent`, nothing to scroll to). */}
                        {parent ? (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => jumpToMessage(parent.id)}
                            onKeyDown={(ev) => {
                              if (ev.key !== 'Enter' && ev.key !== ' ') return;
                              ev.preventDefault();
                              jumpToMessage(parent.id);
                            }}
                            aria-label="Jump to the original message"
                            className="mb-2.5 w-full min-w-[200px] cursor-pointer rounded-[6px] border-l-2 border-[#6C09F7] bg-white/70 px-2.5 py-1.5 text-left hover:bg-white/90"
                          >
                            <p className="text-[11px] font-semibold leading-4 text-[#5A07CE]">
                              {isMine(parent.actor, outgoingActor)
                                ? 'You'
                                : senderDisplayName(parent)}
                            </p>
                            {/* break-words, NOT wrap-anywhere: a short reply
                                ("haha") must not squeeze the preview into a
                                per-character column. whitespace-pre-wrap so
                                a multi-line parent message's own newlines
                                actually wrap across up to 2 lines here
                                instead of collapsing to one run of text —
                                same fix as the staged reply preview above
                                the composer. */}
                            <p className="line-clamp-2 whitespace-pre-wrap break-words text-[12px] leading-4 text-muted-foreground">
                              {replyPreviewText(parent)}
                            </p>
                          </div>
                        ) : null}
                        {/* Media anchor (content moments): time pill, frame
                            thumbnail and labels. Seekable only where a player
                            is wired (onSeekToMoment) and on the current cut. */}
                        {mediaAnchor ? (
                          <MomentBlock
                            anchor={mediaAnchor}
                            bodyEmpty={!e.body.trim()}
                            {...momentFlags(mediaAnchor, currentContentVersionId, onSeekToMoment)}
                            onSeek={onSeekToMoment}
                            onOpenFrame={openFrameFor(e, mediaAnchor)}
                            localFrameUrl={e.local_frame_url}
                            fileSource={fileSource}
                          />
                        ) : null}
                        {/* Quote-reply anchor: the script text this message is about,
                            visually separate from the sender's own words. */}
                        {anchor ? (
                          <div className="mb-2 w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[11px] py-2">
                            {anchor.aspect ? (
                              <div className="mb-1 flex items-center gap-1">
                                <span className="inline-block rounded-full border border-border bg-card px-2 py-px text-[10px] font-medium capitalize text-muted-foreground">
                                  {anchor.aspect.replace(/_/g, ' ')}
                                </span>
                              </div>
                            ) : null}
                            <p
                              role={onJumpToAnchor ? 'button' : undefined}
                              tabIndex={onJumpToAnchor ? 0 : undefined}
                              onClick={onJumpToAnchor ? () => onJumpToAnchor(anchor) : undefined}
                              onKeyDown={
                                onJumpToAnchor
                                  ? (ev) => {
                                      if (ev.key !== 'Enter' && ev.key !== ' ') return;
                                      ev.preventDefault();
                                      onJumpToAnchor(anchor);
                                    }
                                  : undefined
                              }
                              aria-label={onJumpToAnchor ? 'Jump to this line in the script' : undefined}
                              className={cn(
                                // whitespace-pre-wrap: a script anchor can
                                // span a real line break (e.g. a selection
                                // crossing a "Shot:"/"VO:" boundary) — same
                                // fix as the reply previews above.
                                // overflow-wrap:anywhere so a long unbroken
                                // token in the quoted text breaks and wraps
                                // instead of widening the bubble past the panel.
                                'whitespace-pre-wrap wrap-anywhere text-[14px] leading-5 text-[#171717]',
                                onJumpToAnchor && 'cursor-pointer rounded-sm hover:underline',
                                anchor.quoted_text.length > 180 &&
                                  !expandedMsgs.has(`${e.id}-anchor`) &&
                                  'line-clamp-4',
                              )}
                            >
                              &ldquo;{anchor.quoted_text}&rdquo;
                            </p>
                            {anchor.quoted_text.length > 180 ? (
                              <button
                                type="button"
                                onClick={() => toggleExpanded(`${e.id}-anchor`)}
                                className="mt-1 text-[11px] font-medium text-[#5A07CE] hover:underline"
                              >
                                {expandedMsgs.has(`${e.id}-anchor`) ? 'Show less' : 'Show more'}
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                        {/* overflow-wrap:anywhere, NOT break-words: a single
                            long unbroken token (a URL, a pasted hash) must
                            break mid-word to wrap. break-words
                            (overflow-wrap:break-word) doesn't count the break
                            toward min-content sizing, so inside this flex
                            column the bubble kept its full token width and ran
                            off the panel in one strip. `anywhere` does count
                            it, letting the column's max-w-[80%] actually cap
                            and wrap the text. */}
                        {bodyTextShown ? (
                          <p
                            className={cn(
                              'whitespace-pre-wrap wrap-anywhere',
                              isLongBody(e.body) && !expandedMsgs.has(e.id) && 'line-clamp-4',
                            )}
                          >
                            {e.body}
                          </p>
                        ) : null}
                        {isLongBody(e.body) ? (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(e.id)}
                            className="mt-1 text-[13px] font-medium text-[#5A07CE] hover:underline"
                          >
                            {expandedMsgs.has(e.id) ? 'Show less' : 'Read more'}
                          </button>
                        ) : null}
                        {/* The message's files, below its text. */}
                        {attachments.length > 0 ? (
                          <MessageAttachments
                            attachments={attachments}
                            fileSource={fileSource}
                            onOpen={attachmentOpenerFor(attachments, isOptimistic)}
                            className={cn(bodyTextShown && ATTACHMENTS_BELOW_TEXT_CLASS)}
                          />
                        ) : null}
                        {/* Per-bubble time, WhatsApp/Telegram-style — every
                            message carries its own timestamp, not just the
                            first of a consecutive group. */}
                        {timeStr || e.edited ? (
                          <div
                            className={cn(
                              'mt-1 text-right text-[10px] leading-none',
                              mine ? 'text-[#1A0540]/50' : 'text-muted-foreground',
                            )}
                          >
                            {e.edited ? <span className="italic">edited</span> : null}
                            {e.edited && timeStr ? ' · ' : null}
                            {timeStr}
                          </div>
                        ) : null}
                        {/* Reply — pinned to the bubble's own bottom corner,
                            hover-revealed (YouTube/Instagram-style comment
                            affordance), not a line item in the column's
                            normal flow. Industry-standard placement
                            (WhatsApp/Telegram/Messenger): the icon sits on
                            the bubble's INNER edge, toward the column's
                            center — bottom-left for a right-aligned "mine"
                            bubble, bottom-right for a left-aligned "theirs"
                            one — never pinned to a fixed screen side, which
                            would put it flush against the outer edge (past
                            the avatar) for one of the two directions. */}
                        {/* Hover action cluster on the bubble's inner-bottom
                            edge: Reply (all), Edit (server-granted), Delete
                            (server-granted). Grows toward the column centre so
                            it never runs past the outer edge/avatar. Each
                            button is size-6 for WCAG 2.5.8 target size. */}
                        {hasAnyAction(permissions) ? (
                          <div
                            className={cn(
                              'absolute -bottom-2 z-10 flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100',
                              mine ? 'left-1' : 'right-1 flex-row-reverse',
                            )}
                          >
                            {canReplyMsg ? (
                              <button
                                type="button"
                                onClick={() => beginReply(e, senderName)}
                                aria-label="Reply"
                                title="Reply"
                                className="flex size-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
                              >
                                <ArrowBendUpLeft size={13} weight="bold" />
                              </button>
                            ) : null}
                            {canEditMsg ? (
                              <button
                                type="button"
                                onClick={() => beginEdit(e)}
                                aria-label={EDIT_MESSAGE_LABEL}
                                title={EDIT_TITLE}
                                className="flex size-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground"
                              >
                                <PencilSimple size={13} weight="bold" />
                              </button>
                            ) : null}
                            {canDeleteMsg ? (
                              <button
                                type="button"
                                onClick={() => setDeletingId(e.id)}
                                aria-label={DELETE_MESSAGE_LABEL}
                                title={DELETE_TITLE}
                                className="flex size-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm hover:bg-rose-50 hover:text-rose-600"
                              >
                                <Trash size={13} weight="bold" />
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>,
                );
              }
              return rendered;
            })()
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      {readOnly || (canAddComment && onAddComment) ? (
        <div className="shrink-0 bg-card px-[18px] py-[14px] shadow-[0px_-1px_10px_rgba(0,0,0,0.1)]">
          {/* Staged quote-reply chip — dismissible, sits above the composer so
              the input itself stays empty for the sender's own words. */}
          {sendFailed ? (
            <p className="mb-1.5 text-[12px] font-medium text-rose-600">
              Couldn&rsquo;t send — please try again.
            </p>
          ) : null}
          {editing && !readOnly ? (
            // Mirrors the reply chip: a header line + a preview of the ORIGINAL
            // text (the message being edited), while the input below holds the
            // editable copy — WhatsApp-style.
            <div className={COMPOSER_CHIP_CLASS}>
              <div className="min-w-0 flex-1">
                <p className={COMPOSER_CHIP_TITLE_CLASS}>
                  <PencilSimple size={11} weight="bold" />
                  Editing message
                </p>
                <p className="line-clamp-2 whitespace-pre-wrap wrap-anywhere text-[12px] leading-4 text-muted-foreground">
                  {editing.preview}
                </p>
              </div>
              <button
                type="button"
                onClick={cancelEdit}
                aria-label="Cancel edit"
                className={COMPOSER_CHIP_DISMISS_CLASS}
              >
                <X size={12} />
              </button>
            </div>
          ) : null}
          {stagedReply && !editing && !readOnly ? (
            // "Replying to You" read oddly for a reply to your own earlier
            // message — grammatically it addresses the reader, not the
            // target. Dropped the "Replying to" prefix entirely and matched
            // the accent border + purple sender-name styling the SAME quote
            // already gets once sent (see the parent-preview block above):
            // a small reply icon + bare sender name communicates "this is
            // what you're replying to" without needing it spelled out, and
            // the staged chip now looks like a preview of the sent one
            // instead of a differently-styled interim state.
            <div className={COMPOSER_CHIP_CLASS}>
              <div className="min-w-0 flex-1">
                <p className={COMPOSER_CHIP_TITLE_CLASS}>
                  <ArrowBendUpLeft size={11} weight="bold" />
                  {stagedReply.label}
                </p>
                {/* line-clamp-4, NOT a single truncated line — same as the
                    stagedAnchor quote chip below: full text stays in the
                    DOM and CSS does the multi-line clamp, so a short reply
                    isn't the only kind that stays readable at a glance.
                    whitespace-pre-wrap too — same as the actual sent bubble
                    body (see the message <p> below) — without it the
                    browser's default `white-space: normal` collapses a
                    real multi-line message's own newlines into one run of
                    text before line-clamp ever gets a chance to wrap it,
                    so a genuine 4-line message showed as a single
                    (wrong) line here instead of its real 4. */}
                <p className="line-clamp-4 whitespace-pre-wrap break-words text-[12px] leading-4 text-muted-foreground">
                  {stagedReply.preview}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStagedReply(null)}
                aria-label="Cancel reply"
                // size-6 (24px), not size-5 (20px) — WCAG 2.5.8 Target Size
                // minimum, and consistent with the Reply/close icon buttons
                // elsewhere in this file, which are already size-6.
                className={COMPOSER_CHIP_DISMISS_CLASS}
              >
                <X size={12} />
              </button>
            </div>
          ) : null}
          {/* A send in flight took its moment and files when it started, so
              their × stays off until it settles. */}
          {showStagedMomentChip ? (
            <StagedMomentChip staged={stagedMoment} onClear={richComposer.onClear} clearDisabled={sending} />
          ) : null}
          {showFileChips ? (
            <AttachmentChips
              files={stagedFiles}
              progressSource={richComposer?.uploadProgress}
              onRemove={richComposer?.onRemoveFile}
              removeDisabled={sending}
            />
          ) : null}
          {stagedAnchor && !readOnly ? (
            <div className="mb-2 flex items-start gap-2 rounded-[8px] border border-[#e5e5e5] bg-white px-[11px] py-2">
              <div className="min-w-0 flex-1">
                {stagedAnchor.aspect ? (
                  <span className="mb-0.5 inline-block rounded-full border border-border bg-muted px-2 py-px text-[10px] font-medium capitalize text-muted-foreground">
                    {stagedAnchor.aspect.replace(/_/g, ' ')}
                  </span>
                ) : null}
                <p className="line-clamp-4 whitespace-pre-wrap break-words text-[14px] leading-5 text-[#171717]">
                  &ldquo;{stagedAnchor.quoted_text}&rdquo;
                </p>
              </div>
              <button
                type="button"
                onClick={() => onDismissAnchor?.()}
                aria-label="Remove quoted text"
                // size-6 (24px), not size-5 (20px) — WCAG 2.5.8 Target Size
                // minimum, and consistent with the Reply/close icon buttons
                // elsewhere in this file, which are already size-6.
                className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
              >
                <X size={12} />
              </button>
            </div>
          ) : null}
          {showMomentTools ? <div className={MOMENT_TOOLS_ROW_CLASS}>{richComposer?.tools}</div> : null}
          <div className="flex items-end gap-2">
            {attachPicker ? <AttachFilesButton picker={attachPicker} disabled={composerDisabled} /> : null}
            <textarea
              ref={textareaRef}
              rows={1}
              value={draft}
              disabled={composerDisabled}
              title={readOnlyTitle}
              onChange={(ev) => {
                setSendFailed(false);
                setDraft(ev.target.value);
              }}
              onKeyDown={(ev) => {
                // Enter sends; Shift+Enter inserts a newline (industry-
                // standard chat composer behavior — Slack, iMessage, etc).
                if (ev.key === 'Enter' && !ev.shiftKey) {
                  ev.preventDefault();
                  void submit();
                }
              }}
              placeholder={composerPlaceholder}
              className="max-h-[104px] min-h-10 flex-1 resize-none overflow-y-auto rounded-[6px] border border-[#E5E5E5] bg-card px-3 py-2.5 text-body-sm-medium/relaxed placeholder:text-[#737373] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
            />
            <Button
              type="button"
              variant="default"
              size="icon"
              disabled={!sendable || sendBusy || composerDisabled}
              onClick={() => void submit()}
              aria-label="Send message"
              title={readOnlyTitle}
              className="size-10 shrink-0 rounded-full bg-[#18181B] text-white hover:bg-[#18181B]/90 enabled:hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {addCommentBusy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} />
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className="shrink-0 bg-muted/60 px-4 py-3 text-center text-[11px] font-medium text-muted-foreground shadow-[0px_-1px_10px_rgba(0,0,0,0.1)]">
          This conversation is locked. History remains visible above.
        </div>
      )}

      {/* In-app delete confirmation (replaces the native window.confirm). */}
      <ActionConfirmDialog
        open={!!deletingId}
        onOpenChange={(next) => {
          if (!next) setDeletingId(null);
        }}
        variant="delete"
        title="Delete message?"
        description="This message will be permanently deleted for everyone. This can't be undone."
        confirmLabel="Delete"
        busy={deleteBusy}
        onConfirm={confirmDelete}
      />

      {/* The file viewer (a message's files, or a moment's frame), and the
          confirm before a reply drops a staged frame (the same in-app dialog
          as delete). */}
      <FileViewerDialog
        items={viewer?.items ?? NO_VIEWER_ITEMS}
        index={viewer?.index ?? null}
        onIndexChange={changeViewerIndex}
        fileSource={fileSource}
        onGoToFrame={viewerGoToFrame}
      />
      {/* A send in flight is carrying the frame; discarding waits until it settles. */}
      {richComposer ? (
        <ActionConfirmDialog
          open={!!pendingReply}
          onOpenChange={handleReplyDiscardOpenChange}
          variant="delete"
          title={REPLY_DISCARD_TITLE}
          description={REPLY_DISCARD_DESCRIPTION}
          confirmLabel={REPLY_DISCARD_CONFIRM_LABEL}
          canConfirm={!sending}
          onConfirm={confirmDiscardAndReply}
        />
      ) : null}
    </section>
  );
}

function SkeletonBubbles() {
  const rows = [
    { mine: false, w: 'w-3/5' },
    { mine: true, w: 'w-2/5' },
    { mine: false, w: 'w-1/2' },
    { mine: true, w: 'w-3/5' },
    { mine: false, w: 'w-2/5' },
  ];
  return (
    <div className="flex flex-col gap-3" aria-hidden="true">
      {rows.map((r, i) => (
        <div key={i} className={cn('flex flex-col', r.mine ? 'items-end' : 'items-start')}>
          <Skeleton className="mb-1 h-3 w-20 rounded-sm" />
          <Skeleton
            className={cn(
              'h-10 rounded-[8px]',
              r.w,
              r.mine ? 'bg-secondary' : 'bg-muted',
            )}
          />
        </div>
      ))}
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex h-full flex-col items-center justify-center text-center"
    >
      <div className="flex size-10 items-center justify-center rounded-full bg-rose-50 text-rose-500">
        <AlertCircle size={18} />
      </div>
      <p className="mt-3 text-body-sm-semibold text-foreground">Couldn't load thread</p>
      <p className="mt-1 max-w-xs text-caption-regular text-muted-foreground">
        Something went wrong fetching messages. Check your connection and try again.
      </p>
      {onRetry ? (
        <Button
          type="button"
          variant="outline"
          onClick={onRetry}
          className="mt-4 h-auto rounded-full px-3 py-1.5 text-caption-medium"
        >
          <RefreshCw size={12} />
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export default ChatThread;
